import { Link } from "react-router-dom";
import { Mail, Phone, Instagram, Facebook } from "lucide-react";

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useLanguageStore } from "@/stores/languageStore";
import baseiyLogo from "@/assets/baseiy-logo.png";

export const Footer = () => {
  const { t } = useLanguageStore();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-charcoal text-white">
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={baseiyLogo} alt="BASE IY logo" className="h-14 w-auto" />
              <span className="font-display text-2xl font-bold text-gold">BASE IY</span>
            </div>
            <p className="text-sm text-white/70">{t("footer.description")}</p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("footer.quickLinks")}</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.home")}
              </Link>
              <Link to="/products" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.products")}
              </Link>
              <Link to="/about" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.about")}
              </Link>
              <Link to="/contact" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.contact")}
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("footer.contact")}</h3>
            <div className="space-y-3">
              <a href="tel:0547677336" className="flex items-center gap-2 text-sm text-white/70 hover:text-primary transition-colors">
                <Phone className="h-4 w-4 text-primary" />
                <span>054-7677336</span>
              </a>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Mail className="h-4 w-4 text-primary" />
                <span>onlinecovr2000@gmail.com</span>
              </div>
              {/* Social Media Links */}
              <div className="flex items-center gap-4 mt-4">
                <a 
                  href="https://www.tiktok.com/@baseiy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-primary/20 flex items-center justify-center transition-colors group"
                  aria-label="TikTok"
                >
                  <TikTokIcon className="h-5 w-5 text-white/70 group-hover:text-primary transition-colors" />
                </a>
                <a 
                  href="https://www.instagram.com/baseiy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-primary/20 flex items-center justify-center transition-colors group"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5 text-white/70 group-hover:text-primary transition-colors" />
                </a>
                <a 
                  href="https://www.facebook.com/baseiy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-primary/20 flex items-center justify-center transition-colors group"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5 text-white/70 group-hover:text-primary transition-colors" />
                </a>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("footer.newsletterTitle")}</h3>
            <p className="text-sm text-white/70">{t("footer.newsletterSubtitle")}</p>
            <div className="flex gap-2">
              <Input
                placeholder={t("footer.emailPlaceholder")}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
              <Button className="gradient-gold text-white shrink-0">{t("footer.subscribe")}</Button>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-white/10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p>
              © {year} BASE IY. {t("footer.rights")}.
            </p>
            <p className="text-xs text-white/60">
              {t("footer.exemptDealer")}
            </p>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-primary transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-primary transition-colors">
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
